import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';
import { ArChargesTableDataModel } from './ar-charges.model';
import { CommonService, ValidFieldValue } from '../../services/common.service';
import {
  apiCrudActions,
  CrudActions,
  DataTypes,
  hyperlinkLabels,
  MasterLookup,
  popupButtons,
  SingleDeleteHeaderText,
  StatusCodes,
} from '../../utility/constants';
import { URLConstants } from '../../utility/url-constants';
import {
  calendarDateToFormDate,
  toApiDateString,
} from '../../utility/invoice-date.util';
import { BaseService } from 'src/app/core/services/base.httpService';
import { handleErrorMessage } from '../../operators/error.operator';
import {
  arChargeDraftRowHasData,
  arChargeStoreHasIncompletePendingRows,
} from '../charge-grid-save-validation.util';
export interface ArChargesFormObject extends ArChargesTableDataModel {
  isInputRow?: boolean;
  isRowDisable?: boolean;
  action?: string;
}

/** `GET /v1/accountsReceivable/getAllUnpublishedARInvoices` row. */
export interface UnpublishedArInvoice {
  id: number;
  invoiceNumber: string;
  accountNumber: string | number | null;
  accountName: string | null;
  invoiceDate: string | null;
  chargeAmount: number;
}

/** `POST /v1/accountsReceivable/syncApCharges/eligibility` (node SPEC-ARSYNC-001). */
export interface SyncApChargesEligibility {
  hawbId: number;
  canSync: boolean;
  isPublished: boolean;
  publishedInvoiceNumber: string | null;
  isRated: boolean;
  svcType: string | null;
  pendingApCharges: unknown[];
  unmappedApCharges: unknown[];
}

export interface SyncApChargesEligibilityResponse {
  statusCode: number;
  message: string;
  data: SyncApChargesEligibility;
}

export interface SyncApChargesResponse {
  statusCode: number;
  message: string;
  data: {
    hawbId: number;
    created: number;
    syncedApInvoiceChargeIds: number[];
    unmappedApCharges: unknown[];
  };
}

@Injectable({ providedIn: 'root' })
export class ArChargesService {
  private readonly arChargesBehaviorSubject = new BehaviorSubject<
    ArChargesFormObject[]
  >([]);
  private readonly arChargeSavedInViewModeSubject = new Subject<void>();
  private readonly dropdownsLoadedSubject = new BehaviorSubject<boolean>(false);
  private deletedArCharges: ArChargesFormObject[] = [];
  private chargeCodeFieldData: any[] = [];
  private hawbId: number | null = null;
  private hawbExt: string | null = null;
  private mawbId: number | null = null;
  private mawbExt: string | null = null;
  private hasUnsavedChanges = false;
  private chargeGridValidationBlocked = false;
  private isFromHawb: boolean = false;
  private shipmentInvDate: string | null = null;
  private committedShipmentInvDate: string | null = null;
  private readonly errorHandler = handleErrorMessage();

  constructor(
    private readonly commonService: CommonService,
    private readonly baseService: BaseService
  ) {}

  setChargeCodeFieldData(data: any[]) {
    this.chargeCodeFieldData = data || [];
    this.checkAllDropdownsHaveData();
  }

  getChargeCodeFieldData(): any[] {
    return this.chargeCodeFieldData;
  }

  private checkAllDropdownsHaveData(): void {
    const allHaveData = this.chargeCodeFieldData.length > 0;

    if (allHaveData && !this.dropdownsLoadedSubject.value) {
      this.dropdownsLoadedSubject.next(true);
    }
  }

  public get dropdownsLoaded$(): Observable<boolean> {
    return this.dropdownsLoadedSubject.asObservable();
  }

  public setDetailsForARTab(
    id: number | null,
    ext: string | null,
    isFromHawb: boolean
  ) {
    if (isFromHawb) {
      this.hawbId = id;
      this.hawbExt = ext;
    } else {
      this.mawbId = id;
      this.mawbExt = ext;
    }
    this.isFromHawb = isFromHawb;
  }

  public getDetailsForARTab(): {
    id: number | null;
    ext: string | null;
    isFromHawb: boolean;
  } {
    return {
      id: this.isFromHawb ? this.hawbId : this.mawbId,
      ext: this.isFromHawb ? this.hawbExt : this.mawbExt,
      isFromHawb: this.isFromHawb,
    };
  }

  public setARTabDetailsCollection(arData: any[]) {
    const processedData = this.processGroupedARInvoiceData(arData);
    this.deletedArCharges = [];
    this.hasUnsavedChanges = false;
    this.shipmentInvDate =
      processedData.find(
        row => this.isUnInvoicedShipmentCharge(row.arInvoiceId) && row.invDate
      )?.invDate ??
      toApiDateString(new Date());
    this.committedShipmentInvDate = this.shipmentInvDate;
    this.arChargesBehaviorSubject.next(processedData);
  }

  public setShipmentInvoicedDate(invDate: string): void {
    this.shipmentInvDate = invDate;
    this.hasUnsavedChanges = true;
  }

  public commitShipmentInvoicedDate(): void {
    this.committedShipmentInvDate = this.shipmentInvDate;
  }

  public revertShipmentInvoicedDate(): void {
    this.shipmentInvDate = this.committedShipmentInvDate;
  }

  private isUnInvoicedShipmentCharge(arInvoiceId: unknown): boolean {
    return this.isFromHawb && !arInvoiceId;
  }

  public patchUnInvoicedInvoiceDate(row: AbstractControl): void {
    if (this.isUnInvoicedShipmentCharge(row.get('arInvoiceId')?.value)) {
      row
        .get('invoiceDate')
        ?.setValue(calendarDateToFormDate(this.shipmentInvDate), {
          emitEvent: false,
        });
    }
  }

  public applyInvoiceDateState(row: AbstractControl): void {
    this.patchUnInvoicedInvoiceDate(row);
    const control = row.get('invoiceDate');
    if (this.isUnInvoicedShipmentCharge(row.get('arInvoiceId')?.value)) {
      control?.enable({ emitEvent: false });
    } else {
      control?.disable({ emitEvent: false });
    }
  }

  public updateARChargesCollection(charges: ArChargesFormObject[]) {
    this.hasUnsavedChanges = true;
    this.arChargesBehaviorSubject.next(charges);
  }

  public updateDeletedARCharges(deletedCharges: ArChargesFormObject[]) {
    this.deletedArCharges = deletedCharges;
    if (deletedCharges.length > 0) {
      this.hasUnsavedChanges = true;
    }
  }

  public getDeletedArCharges(): ArChargesFormObject[] {
    return [...this.deletedArCharges];
  }

  public getHasUnsavedChanges(): boolean {
    return this.hasUnsavedChanges;
  }

  public resetUnsavedChanges(): void {
    this.hasUnsavedChanges = false;
  }

  public setChargeGridValidationBlocked(blocked: boolean): void {
    this.chargeGridValidationBlocked = blocked;
  }

  public getChargeGridValidationBlocked(): boolean {
    return this.chargeGridValidationBlocked;
  }

  public hasIncompletePendingChargeRowsInStore(): boolean {
    return arChargeStoreHasIncompletePendingRows(
      this.arChargesBehaviorSubject.getValue()
    );
  }

  private processGroupedARInvoiceData(
    groupedInvoiceData: any[]
  ): ArChargesFormObject[] {
    const arChargesCollection: ArChargesFormObject[] = [];
    groupedInvoiceData.forEach(invoice => {
      if (invoice.arCharges && Array.isArray(invoice.arCharges)) {
        invoice.arCharges.forEach((charge: any) => {
          const resolvedArInvoiceId =
            Number(charge.arInvoiceId) ||
            Number(invoice.id) ||
            Number(invoice.arInvoiceId) ||
            0;
          const arChargeObj: ArChargesFormObject = {
            id: charge.id || 0,
            arInvoiceId: resolvedArInvoiceId,
            invoiceNumber: charge.invoiceNumber ?? '',
            invoiceDate: charge.invoiceDate ?? '',
            published: invoice.isPublish ? 'Yes' : 'No',
            chargeCode: charge.chargeCode ?? '',
            chargeCodeId: charge.ibcChargeCodeId || charge.chargeCodeId || null,
            chargeDescription: charge.chargeDescription ?? '',
            chargeAmount: charge.chargeAmount ?? 0,
            shipmentIdentifierValue: charge.shipmentIdentifierValue ?? '',
            shipmentIdentifier: charge.shipmentIdentifier ?? '',
            shipmentIdentifierId: charge.shipmentIdentifierId ?? null,
            tempId: this.commonService.generateUniqueTempId(),
            isInputRow: false,
            action: charge.id && charge.id > 0 ? 'u' : 'c',
            invDate: charge.invDate ?? null,
          };
          arChargesCollection.push(arChargeObj);
        });
      }
    });
    return arChargesCollection;
  }

  public get getARTabDetailsCollection$(): Observable<ArChargesFormObject[]> {
    return this.arChargesBehaviorSubject.asObservable();
  }

  public getTransformedData(): ArChargesFormObject[] {
    const items = this.arChargesBehaviorSubject.getValue();
    return items.map((item: ArChargesFormObject) => {
      const transformedItem = { ...item };
      if (transformedItem.chargeCodeId) {
        const chargeCode = this.chargeCodeFieldData.find(
          (cc: any) => Number(cc.id) === Number(transformedItem.chargeCodeId)
        );
        if (chargeCode) {
          transformedItem.chargeCode = chargeCode.code || chargeCode.name;
        }
      }
      return transformedItem;
    });
  }

  private hasInvoiceNumber(row: { invoiceNumber?: unknown }): boolean {
    const n = row?.invoiceNumber;
    if (!this.commonService.isValidField(n as ValidFieldValue)) {
      return false;
    }
    if (typeof n === 'string') {
      return n.trim().length > 0;
    }
    return String(n).trim().length > 0;
  }

  private normalizeActionToApiFormat(action: string | undefined): string {
    if (!action) {
      return apiCrudActions.create;
    }
    switch (action) {
      case CrudActions.create:
      case CrudActions.add:
        return apiCrudActions.create;
      case CrudActions.update:
        return apiCrudActions.update;
      case CrudActions.delete:
        return apiCrudActions.delete;
      case apiCrudActions.create:
      case apiCrudActions.update:
      case apiCrudActions.delete:
        return action;
      default:
        return action || apiCrudActions.create;
    }
  }

  private buildArChargesPayload(
    arChargesData: ArChargesFormObject[],
    parentDetails: any,
    options?: {
      isAdd?: boolean;
      useRowAction?: boolean;
    }
  ): any[] {
    const groupedByInvoice = arChargesData.reduce(
      (acc: any, row: ArChargesFormObject) => {
        const invoiceId = row.arInvoiceId || 0;
        if (!acc[invoiceId]) {
          acc[invoiceId] = [];
        }
        acc[invoiceId].push(row);
        return acc;
      },
      {}
    );
    const result: any[] = [];
    for (const invoiceId in groupedByInvoice) {
      const charges = groupedByInvoice[invoiceId];
      if (charges.length === 0) continue;
      const firstCharge = charges[0];
      const shipmentIdOrMawbId = parentDetails.id || 0;
      const payload: any = {
        invoiceNumber: firstCharge.invoiceNumber || '',
        invoiceDate: this.hasInvoiceNumber(firstCharge)
          ? firstCharge.invoiceDate || ''
          : '',
        isFromShipment: parentDetails.isFromHawb,
        shipmentIdOrMawbId: Number(shipmentIdOrMawbId) || 0,
        arCharges: charges.map((row: ArChargesFormObject) => {
          let id: number;
          let action: string;

          if (options?.isAdd === undefined) {
            id = row.id || 0;
            action = this.normalizeActionToApiFormat(row.action);
          } else {
            id = options.isAdd ? 0 : row.id || 0;
            action = options.isAdd
              ? apiCrudActions.create
              : apiCrudActions.update;
          }

          const chargeRow: any = {
            id: id,
            action: action,
            ibcChargeCodeId: row.chargeCodeId || 0,
            chargeCode: row.chargeCode || '',
            chargeDescription: row.chargeDescription || '',
            chargeAmount: Number.parseFloat(
              row.chargeAmount?.toString() || '0'
            ),
            shipmentIdentifier: parentDetails.isFromHawb
              ? SingleDeleteHeaderText.Shipment
              : SingleDeleteHeaderText.MawbCaps,
            shipmentIdentifierId: row.shipmentIdentifierId || 0,
            shipmentIdentifierValue: this.commonService.isValidField(
              row.shipmentIdentifierValue ?? ''
            )
              ? row.shipmentIdentifierValue
              : parentDetails.ext ?? '',
            shipmentIdOrMawbId: Number(shipmentIdOrMawbId) || 0,
          };
          if (this.hasInvoiceNumber(row)) {
            chargeRow.arInvoiceId = row.arInvoiceId || null;
          }
          if (this.isUnInvoicedShipmentCharge(row.arInvoiceId) && this.shipmentInvDate) {
            chargeRow.invDate = this.shipmentInvDate;
          }
          return chargeRow;
        }),
      };
      if (this.hasInvoiceNumber(firstCharge)) {
        payload.arInvoiceId = firstCharge.arInvoiceId || null;
      }
      result.push(payload);
    }
    return result;
  }

  public buildPayloadForSaveOrUpdateARChargeInViewMode(
    arChargesData: any[],
    isAdd: boolean
  ): any {
    if (!arChargesData || arChargesData.length === 0) {
      return null;
    }
    const parentDetails = this.getDetailsForARTab();
    const result = this.buildArChargesPayload(arChargesData, parentDetails, {
      isAdd: isAdd,
    });
    return { ar: result };
  }

  public buildPayloadForDeleteARChargeInViewMode(selectedCharges: any[]): any {
    if (!selectedCharges || selectedCharges.length === 0) {
      return null;
    }

    const groupedByInvoice = selectedCharges.reduce(
      (acc: any, row: ArChargesFormObject) => {
        const invoiceId = row.arInvoiceId || 0;
        if (!acc[invoiceId]) {
          acc[invoiceId] = [];
        }
        acc[invoiceId].push(row);
        return acc;
      },
      {}
    );

    const result: any[] = [];
    const parentDetails = this.getDetailsForARTab();
    for (const invoiceId in groupedByInvoice) {
      const charges = groupedByInvoice[invoiceId];
      if (charges.length === 0) continue;

      const payload: any = {
        arCharges: charges.map((row: ArChargesFormObject) => {
          const chargePayload: any = {
            action: CrudActions.delete,
            shipmentIdentifierId: row.shipmentIdentifierId || 0,
            id: row.id || 0,
          };
          if (!parentDetails.isFromHawb) {
            chargePayload.isFromShipment = false;
            chargePayload.shipmentIdOrMawbId = parentDetails.id ?? 0;
          }
          return chargePayload;
        }),
      };
      if (this.hasInvoiceNumber(charges[0])) {
        payload.arInvoiceId = Number(invoiceId) || 0;
      }
      if (!parentDetails.isFromHawb) {
        payload.isFromShipment = false;
        payload.shipmentIdOrMawbId = parentDetails.id ?? 0;
      }
      result.push(payload);
    }

    return { ar: result };
  }

  get isInputRowAdded(): boolean {
    return this.arChargesBehaviorSubject
      .getValue()
      .some((x: ArChargesFormObject) => x.isInputRow);
  }

  get arChargesCollection$(): Observable<ArChargesFormObject[]> {
    return this.arChargesBehaviorSubject
      .asObservable()
      .pipe(
        map(items =>
          items.filter(
            (row: ArChargesFormObject) => row.action !== CrudActions.delete
          )
        )
      );
  }

  get arChargesCollection(): any[] {
    // Committed rows plus draft rows with values (saved with Shipment/MAWB); blank drafts are dropped.
    const rows = this.arChargesBehaviorSubject
      .getValue()
      .filter((row: ArChargesFormObject) => {
        return (
          row.isRowDisable ||
          (row.isRowDisable === false && arChargeDraftRowHasData({ ...row }))
        );
      });
    const allRows = [...rows, ...this.deletedArCharges];
    const parentDetails = this.getDetailsForARTab();
    return this.buildArChargesPayload(allRows, parentDetails, {
      useRowAction: true,
    });
  }

  public getArChargesForSave(
    shipmentId?: number,
    shipmentIdentifier?: string
  ): any[] {
    const charges = this.filterValidChargesForSave();

    if (charges.length === 0) {
      return [];
    }

    const invoiceMap = this.buildInvoiceMap(
      charges,
      shipmentId,
      shipmentIdentifier
    );
    return this.buildResultFromInvoiceMap(invoiceMap, charges);
  }

  private filterValidChargesForSave(): ArChargesFormObject[] {
    return this.arChargesCollection.filter((charge: ArChargesFormObject) => {
      if (
        charge.action === CrudActions.delete &&
        (!charge.id || charge.id === 0)
      ) {
        return false;
      }
      return [
        CrudActions.add,
        CrudActions.update,
        CrudActions.delete,
        CrudActions.create,
      ].includes(charge.action || '');
    });
  }

  private buildInvoiceMap(
    charges: ArChargesFormObject[],
    shipmentId?: number,
    shipmentIdentifier?: string
  ): Map<string, any[]> {
    const invoiceMap = new Map<string, any[]>();

    for (const arCharge of charges) {
      const arConverted = this.convertDisplayNamesToIds(arCharge);
      const invoiceKey = this.getInvoiceKey(arConverted);

      if (!invoiceMap.has(invoiceKey)) {
        invoiceMap.set(invoiceKey, []);
      }

      const action = this.normalizeAction(arConverted.action);
      const arChargeItem = this.buildChargeItem(
        arConverted,
        action,
        shipmentId,
        shipmentIdentifier
      );

      this.setShipmentIdentifierId(arChargeItem, action, arConverted);
      this.setInvoiceChargesId(arChargeItem, action, arConverted);

      invoiceMap.get(invoiceKey)!.push(arChargeItem);
    }

    return invoiceMap;
  }

  private getInvoiceKey(converted: any): string {
    return converted.arInvoiceId
      ? `invoice_${converted.arInvoiceId}`
      : `invoice_${converted.invoiceNumber || 'unknown'}`;
  }

  private normalizeAction(
    action: string | undefined = CrudActions.add
  ): string {
    if (action === CrudActions.add || action === CrudActions.create) {
      return apiCrudActions.create;
    } else if (action === CrudActions.update) {
      return apiCrudActions.update;
    } else if (action === CrudActions.delete) {
      return apiCrudActions.delete;
    }
    return action || apiCrudActions.create;
  }

  private buildChargeItem(
    converted: any,
    action: string,
    shipmentId?: number,
    shipmentIdentifier?: string
  ): any {
    const chargeCode = this.getChargeCode(converted);
    const chargeShipmentIdentifier =
      shipmentIdentifier ||
      converted.shipmentIdentifier ||
      hyperlinkLabels.hawb;

    const shipmentIdentifierValue = this.getShipmentIdentifierValue(
      action,
      converted,
      chargeShipmentIdentifier
    );

    const chargeItem: any = {
      shipmentIdentifier: chargeShipmentIdentifier,
      shipmentIdentifierValue: shipmentIdentifierValue,
      chargeCode: chargeCode,
      chargeDescription: converted.chargeDescription || '',
      chargeAmount: Number.parseFloat(converted.chargeAmount) || 0,
      shipmentIdOrMawbId: shipmentId || converted.shipmentIdOrMawbId || 0,
      masterLookupDetailId: converted.masterLookupDetailId || 0,
      ibcChargeCodeId: converted.ibcChargeCodeId || converted.chargeCodeId || 0,
      shipmentIdentifierId: converted.shipmentIdentifierId || 0,
      id: converted.id || 0,
      action: action,
    };
    if (this.hasInvoiceNumber(converted)) {
      chargeItem.arInvoiceId = converted.arInvoiceId || 0;
    }

    return chargeItem;
  }

  public getChargeCode(converted: any): string {
    const chargeCode = this.chargeCodeFieldData.find(
      (cc: any) => cc.id === converted.chargeCodeId
    );
    return chargeCode
      ? chargeCode.code || chargeCode.name
      : converted.chargeCode || '';
  }

  public getShipmentIdentifierValue(
    action: string,
    converted: any,
    chargeShipmentIdentifier: string
  ): string {
    let shipmentIdentifierValue = converted.shipmentIdentifierValue || '';
    if (
      (action === apiCrudActions.create ||
        action === CrudActions.create ||
        action === CrudActions.add ||
        CrudActions.update ||
        !converted.id ||
        converted.id === 0) &&
      !shipmentIdentifierValue
    ) {
      shipmentIdentifierValue =
        chargeShipmentIdentifier === hyperlinkLabels.mawb
          ? this.getArMawbExt() || ''
          : this.getArHawbExt() || '';
    }
    return shipmentIdentifierValue;
  }

  private setShipmentIdentifierId(
    chargeItem: any,
    action: string,
    converted: any
  ): void {
    const shipmentIdentifierId =
      chargeItem.shipmentIdentifierId || converted.shipmentIdentifierId;
    const hasValidId = shipmentIdentifierId && shipmentIdentifierId > 0;

    if (hasValidId) {
      chargeItem.shipmentIdentifierId = shipmentIdentifierId;
    }
  }

  private setInvoiceChargesId(
    chargeItem: any,
    action: string,
    converted: any
  ): void {
    const invoiceChargesId = converted.id;
    if (invoiceChargesId) {
      chargeItem.id = invoiceChargesId;
    }
  }

  private buildResultFromInvoiceMap(
    invoiceMap: Map<string, any[]>,
    charges: ArChargesFormObject[]
  ): any[] {
    const result: any[] = [];
    for (const [invoiceKey, chargeItems] of invoiceMap) {
      const firstCharge = this.findChargeByInvoiceKey(charges, invoiceKey);

      if (firstCharge) {
        const converted = this.convertDisplayNamesToIds(firstCharge);
        const group: any = {
          invoiceNumber: converted.invoiceNumber || '',
          invoiceDate: converted.invoiceDate || '',
          arCharges: chargeItems,
        };
        if (this.hasInvoiceNumber(converted)) {
          group.arInvoiceId = converted.arInvoiceId || 0;
        }
        result.push(group);
      }
    }
    return result;
  }

  private findChargeByInvoiceKey(
    charges: ArChargesFormObject[],
    invoiceKey: string
  ): ArChargesFormObject | undefined {
    return charges.find(c => {
      const converted = this.convertDisplayNamesToIds(c);
      const key = this.getInvoiceKey(converted);
      return key === invoiceKey;
    });
  }

  remove(id: number | string) {
    const items = this.arChargesBehaviorSubject.getValue();
    const updated = items.map((x: any) => {
      if (x.id === id || x.tempId === id.toString()) {
        x.action = CrudActions.delete;
      }

      return structuredClone(x);
    });
    this.arChargesBehaviorSubject.next(updated);
    this.recalculateTotalRow();
  }

  triggerEditMode(id: number | string, inputRow = true) {
    const val = inputRow
      ? this.arChargesBehaviorSubject
          .getValue()
          .filter((x: ArChargesFormObject) => !x.isInputRow)
      : this.arChargesBehaviorSubject.getValue();
    const index = val.findIndex(
      (x: ArChargesFormObject) => x.id == id || x.tempId == id
    );
    if (index >= 0) {
      val[index].isInputRow = inputRow;
      this.arChargesBehaviorSubject.next(val);
    }
  }

  private parseChargeAmount(chargeAmount: any): number {
    if (typeof chargeAmount === DataTypes.Number) {
      return chargeAmount;
    }
    if (typeof chargeAmount === DataTypes.String) {
      const cleaned = chargeAmount.replaceAll(/[$,\s]/g, '');
      const parsed = Number.parseFloat(cleaned);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  createTotalRow(data: any[]): any {
    const total = data.reduce((sum, row) => {
      const value = this.parseChargeAmount(row.chargeAmount);
      return sum + value;
    }, 0);

    const totalRow = {
      id: '',
      arInvoiceId: '',
      invoiceNumber: '',
      invoiceDate: '',
      published: '',
      chargeCode: '',
      chargeDescription: 'Total',
      chargeAmount: `$ ${total.toFixed(2)}`,
      isInputRow: false,
      tempId: 'total_row',
    };

    return totalRow;
  }

  recalculateTotalRow(): void {
    const items = this.arChargesBehaviorSubject.getValue();

    const itemsWithoutTotal = items.filter(
      (row: ArChargesFormObject) => !this.isTotalRow(row)
    );

    const validRows = itemsWithoutTotal.filter((row: ArChargesFormObject) => {
      return (
        !row.isInputRow &&
        row.action !== CrudActions.delete &&
        !this.isTotalRow(row)
      );
    });

    const updatedItems = [...itemsWithoutTotal];

    if (validRows.length > 0) {
      const newTotalRow = this.createTotalRow(validRows);
      updatedItems.push(newTotalRow);
    }

    this.arChargesBehaviorSubject.next(updatedItems);
  }

  setCollection(collection: any[]) {
    const totalrow = this.createTotalRow(collection);
    const data = [...collection, totalrow];
    this.arChargesBehaviorSubject.next(
      data.map((x: ArChargesTableDataModel) => ({
        ...x,
        isInputRow: false,
        tempId: x.id?.toString() || '',
      }))
    );
  }

  setArHawbExt(hawbExt: string | null) {
    this.hawbExt = hawbExt;
  }

  getArHawbExt(): string | null {
    return this.hawbExt;
  }

  setArMawbId(mawbId: number | null) {
    this.mawbId = mawbId;
  }

  getArMawbId(): number | null {
    return this.mawbId;
  }

  setArMawbExt(mawbExt: string | null) {
    this.mawbExt = mawbExt;
  }

  getArMawbExt(): string | null {
    return this.mawbExt;
  }

  public isTotalRow(row: ArChargesFormObject): boolean {
    const hasEmptyId =
      !row.id || row.id === 0 || (typeof row.id === 'string' && row.id === '');
    const hasEmptyArInvoiceId =
      !row.arInvoiceId ||
      row.arInvoiceId === 0 ||
      (typeof row.arInvoiceId === 'string' && row.arInvoiceId === '');

    return (
      row.tempId === 'total_row' ||
      (row.chargeDescription === 'Total' && hasEmptyId && hasEmptyArInvoiceId)
    );
  }

  add(row: ArChargesFormObject) {
    const items = this.arChargesBehaviorSubject.getValue();
    const itemsWithoutTotal = items.filter(
      (x: ArChargesFormObject) => !this.isTotalRow(x)
    );

    const inputRows = itemsWithoutTotal.filter(
      (x: ArChargesFormObject) => x.isInputRow
    );
    const savedRows = itemsWithoutTotal.filter(
      (x: ArChargesFormObject) =>
        !x.isInputRow && x.action !== CrudActions.delete
    );

    let updatedItems: ArChargesFormObject[];
    if (row.isInputRow) {
      updatedItems = [row, ...savedRows];
    } else {
      updatedItems = [...inputRows, ...savedRows, row];
    }

    const validRows = updatedItems.filter((r: ArChargesFormObject) => {
      return (
        !r.isInputRow && r.action !== CrudActions.delete && !this.isTotalRow(r)
      );
    });

    if (validRows.length > 0) {
      const newTotalRow = this.createTotalRow(validRows);
      updatedItems.push(newTotalRow);
    }

    this.arChargesBehaviorSubject.next(updatedItems);
  }

  update(id: number | string, updated: ArChargesFormObject) {
    const items = this.arChargesBehaviorSubject.getValue();
    const itemsWithoutTotal = items.filter(
      (x: ArChargesFormObject) => !this.isTotalRow(x)
    );
    const index = itemsWithoutTotal.findIndex(
      (x: ArChargesFormObject) => x.id === id || x.tempId === id
    );
    if (index >= 0) {
      itemsWithoutTotal[index] = { ...itemsWithoutTotal[index], ...updated };
      const validRows = itemsWithoutTotal.filter((r: ArChargesFormObject) => {
        return (
          !r.isInputRow &&
          r.action !== CrudActions.delete &&
          !this.isTotalRow(r)
        );
      });

      const updatedItems = [...itemsWithoutTotal];
      if (validRows.length > 0) {
        const newTotalRow = this.createTotalRow(validRows);
        updatedItems.push(newTotalRow);
      }

      this.arChargesBehaviorSubject.next(updatedItems);
    }
  }

  removeInputRowByTempId(tempId: string) {
    const val = this.arChargesBehaviorSubject.getValue();
    const updated = val.filter((x: ArChargesFormObject) => x.tempId !== tempId);
    this.arChargesBehaviorSubject.next(updated);
  }

  private groupInvoicesById(arList: any[]): Map<number, any[]> {
    const grouped = new Map<number, any[]>();
    for (const inv of arList) {
      const invoiceId = inv.arInvoiceId ?? inv.id;
      if (invoiceId) {
        const bucket = grouped.get(invoiceId) ?? [];
        bucket.push(inv);
        grouped.set(invoiceId, bucket);
      }
    }
    return grouped;
  }

  private sortInvoicesByDate(invoices: any[]): void {
    invoices.sort((a, b) => {
      const dA = new Date(a.invoiceDate || 0).getTime();
      const dB = new Date(b.invoiceDate || 0).getTime();
      return dB - dA;
    });
  }

  private createChargeObject(
    ch: any,
    newest: any,
    chargeId: number,
    defaultShipmentIdentifier: string
  ): ArChargesFormObject {
    const arInvoiceId = ch.arInvoiceId ?? newest.arInvoiceId ?? newest.id;
    const arInvoiceIdNum = arInvoiceId ? Number(arInvoiceId) : 0;
    const parsedChargeAmount = this.parseChargeAmount(ch.chargeAmount);
    const publishedValue = newest.isPublish ?? newest.published ?? false;
    const publishedBooleanDisplay = publishedValue
      ? popupButtons.Yes
      : popupButtons.No;
    const publishedDisplay =
      typeof publishedValue === 'boolean'
        ? publishedBooleanDisplay
        : publishedValue;
    const chargeObject: any = {
      id: chargeId,
      arInvoiceId: arInvoiceIdNum,
      invoiceNumber: newest.invoiceNumber ?? '',
      invoiceDate: newest.invoiceDate ?? '',
      published: publishedDisplay,
      chargeCode: ch.chargeCode ?? '',
      chargeDescription: ch.chargeDescription ?? '',
      chargeAmount: parsedChargeAmount,
      shipmentIdentifier: ch.shipmentIdentifier ?? defaultShipmentIdentifier,
      invoiceChargesId: ch.id ?? undefined,
      shipmentIdentifierId: ch.shipmentIdentifierId ?? undefined,
      chargeCodeId: ch.ibcChargeCodeId ?? ch.chargeCodeId ?? undefined,
      isInputRow: false,
      tempId: chargeId.toString(),
    };

    if (ch.masterLookupDetailId !== undefined) {
      chargeObject.masterLookupDetailId = ch.masterLookupDetailId;
    }
    if (ch.ibcChargeCodeId !== undefined) {
      chargeObject.ibcChargeCodeId = ch.ibcChargeCodeId;
    }
    if (ch.shipmentIdentifierValue !== undefined) {
      chargeObject.shipmentIdentifierValue = ch.shipmentIdentifierValue;
    }
    if (ch.shipmentIdOrMawbId !== undefined) {
      chargeObject.shipmentIdOrMawbId = ch.shipmentIdOrMawbId;
    }

    return chargeObject;
  }

  private processChargesForInvoice(
    invoices: any[],
    seenChargeIds: Set<number>,
    result: ArChargesFormObject[],
    defaultShipmentIdentifier: string
  ): void {
    this.sortInvoicesByDate(invoices);
    const newest = invoices[0];
    const charges = newest.arCharges || [];

    for (const ch of charges) {
      const chargeId = ch.id ?? 0;

      if (chargeId > 0 && seenChargeIds.has(chargeId)) {
        continue;
      }

      if (chargeId > 0) {
        seenChargeIds.add(chargeId);
      }

      const chargeObject = this.createChargeObject(
        ch,
        newest,
        chargeId,
        defaultShipmentIdentifier
      );
      result.push(chargeObject);
    }
  }

  private filterArChargesInternal(
    arList: any[],
    defaultShipmentIdentifier: string,
    logWarning = false
  ): ArChargesFormObject[] {
    if (!Array.isArray(arList) || arList.length === 0) {
      if (logWarning) {
        console.warn('filterArCharges: empty or invalid input');
      }
      return [];
    }

    const grouped = this.groupInvoicesById(arList);
    const result: ArChargesFormObject[] = [];
    const seenChargeIds = new Set<number>();

    for (const invoices of grouped.values()) {
      this.processChargesForInvoice(
        invoices,
        seenChargeIds,
        result,
        defaultShipmentIdentifier
      );
    }

    return result;
  }

  public filterArCharges(arList: any[]): ArChargesFormObject[] {
    return this.filterArChargesInternal(arList, hyperlinkLabels.hawb, true);
  }

  public filterMawbArCharges(arList: any[]): ArChargesFormObject[] {
    return this.filterArChargesInternal(arList, hyperlinkLabels.mawb, false);
  }

  public getChargeCodeOptions(): Observable<any> {
    return this.commonService.getMasterLookUp(MasterLookup.ibcChargeCode).pipe(
      map((data: any) => {
        if (data.statusCode === StatusCodes.Success) {
          return { statusCode: data.statusCode, data: data.data };
        }
        return { statusCode: data.statusCode, data: [] };
      })
    );
  }

  public validateInvoiceNumber(invoiceNumber: string): Observable<any> {
    const url = `${URLConstants.arInvoiceValidate}/${invoiceNumber}`;
    return this.baseService.initiateApiRequest(
      'GET',
      url,
      invoiceNumber,
      'json'
    );
  }

  public convertDisplayNamesToIds(formValue: any): any {
    const converted = { ...formValue };
    this.convertChargeCode(converted);
    return converted;
  }

  private convertChargeCode(converted: any): void {
    if (converted.chargeCodeId) {
      this.setChargeDescriptionIfMissing(converted, converted.chargeCodeId);
      return;
    }

    if (!converted.chargeCode) {
      return;
    }

    if (typeof converted.chargeCode === DataTypes.String) {
      this.convertChargeCodeFromString(converted);
    } else if (typeof converted.chargeCode === DataTypes.Number) {
      converted.chargeCodeId = converted.chargeCode;
      this.setChargeDescriptionIfMissing(converted, converted.chargeCode);
    }
  }

  private setChargeDescriptionIfMissing(
    converted: any,
    chargeCodeId: number
  ): void {
    if (!converted.chargeDescription && this.chargeCodeFieldData.length > 0) {
      const chargeCode = this.chargeCodeFieldData.find(
        (cc: any) => cc.id === chargeCodeId
      );
      converted.chargeDescription = chargeCode?.description || chargeCode?.name;
    }
  }

  private convertChargeCodeFromString(converted: any): void {
    const chargeCode = this.chargeCodeFieldData.find(
      (cc: any) =>
        cc.name === converted.chargeCode || cc.code === converted.chargeCode
    );
    if (chargeCode) {
      converted.chargeCodeId = chargeCode.id;
      if (chargeCode.description || chargeCode.name) {
        converted.chargeDescription = chargeCode.description || chargeCode.name;
      }
    }
  }

  public createArCharge(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('PUT', URLConstants.createARcharges, payload, 'json')
      .pipe(this.errorHandler());
  }

  public updateArCharge(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.updateARcharges, payload, 'json')
      .pipe(this.errorHandler());
  }

  public deleteArCharge(payload: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.deleteARcharges,
      payload,
      'json'
    );
  }

  /** Unpublished AR invoices for the Shipment / MAWB AR tab Invoice Number type-ahead (PAC-24768 AC1). */
  public getUnpublishedArInvoices(): Observable<UnpublishedArInvoice[]> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.getAllUnpublishedARInvoices,
        {},
        'json'
      )
      .pipe(
        this.errorHandler(),
        map((response: any) =>
          response?.statusCode === StatusCodes.Success &&
          Array.isArray(response?.data)
            ? (response.data as UnpublishedArInvoice[])
            : []
        )
      );
  }

  public getSyncApChargesEligibility(
    hawbId: number
  ): Observable<SyncApChargesEligibilityResponse> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.syncApChargesEligibility,
        { hawbId },
        'json'
      )
      .pipe(this.errorHandler());
  }

  public syncApCharges(hawbId: number): Observable<SyncApChargesResponse> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.syncApCharges,
        { hawbId },
        'json'
      )
      .pipe(this.errorHandler());
  }

  public clear(): void {
    this.arChargesBehaviorSubject.next([]);
    this.chargeCodeFieldData = [];
    this.hawbId = null;
    this.hawbExt = null;
    this.mawbId = null;
    this.mawbExt = null;
    this.shipmentInvDate = null;
    this.committedShipmentInvDate = null;
    this.chargeGridValidationBlocked = false;
  }

  /**
   * Observable to subscribe to when AR charge is saved in view mode
   * Parent components (HAWB/MAWB) should subscribe to refresh data
   */
  get arChargeSavedInViewMode$(): Observable<void> {
    return this.arChargeSavedInViewModeSubject.asObservable();
  }
}
